import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { readingPlanService } from '../../../services/supabaseService';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReadingProgressTracker = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activePlan, setActivePlan] = useState(null);
  const [upcomingReadings, setUpcomingReadings] = useState([]);
  const [readingStats, setReadingStats] = useState(null);
  const [weeklyProgress, setWeeklyProgress] = useState({ completed: 0, total: 7 });

  useEffect(() => {
    if (user?.id) {
      fetchReadingProgress();
    }
  }, [user?.id]);

  const fetchReadingProgress = async () => {
    setLoading(true);
    try {
      const { data: enrolledPlans, error } = await readingPlanService?.getUserReadingPlans(user?.id);
      if (error) {
        console.error('Error fetching reading plans:', error);
        return;
      }

      const activeEnrollment = enrolledPlans?.find(p => p?.status === 'in_progress');

      if (activeEnrollment) {
        setActivePlan(activeEnrollment);
        const weeklyStats = calculateWeeklyProgress(activeEnrollment);
        setWeeklyProgress(weeklyStats);
        await Promise.all([
          fetchUpcomingReadings(activeEnrollment),
          fetchReadingStats(activeEnrollment)
        ]);
      } else {
        setActivePlan(null);
      }
    } catch (error) {
      console.error('Error loading reading progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateWeeklyProgress = (enrollment) => {
    const daysCompleted = Math.floor((enrollment?.completed_days || 0) % 7);
    return { completed: daysCompleted, total: 7 };
  };

  const fetchUpcomingReadings = async (enrollment) => {
    try {
      const currentDay = enrollment?.current_day || 1;
      const totalDays = enrollment?.reading_plans?.duration_days || 365;
      const daysToFetch = Math.min(5, totalDays - currentDay + 1);
      if (daysToFetch <= 0) return;

      const { data: enrollmentData } = await supabase
        ?.from('user_reading_plans')
        ?.select('reading_plan_id')
        ?.eq('id', enrollment?.id)
        ?.single();

      if (!enrollmentData?.reading_plan_id) return;

      const dayNumbers = Array.from({ length: daysToFetch }, (_, i) => currentDay + i);

      const { data: daysData } = await supabase
        ?.from('reading_plan_days')
        ?.select(`
          day_number,
          title,
          reading_plan_references (
            reference_text,
            display_order
          )
        `)
        ?.eq('reading_plan_id', enrollmentData?.reading_plan_id)
        ?.in('day_number', dayNumbers)
        ?.order('day_number');

      if (daysData) {
        const readings = daysData?.map((day, idx) => {
          const refs = day?.reading_plan_references
            ?.sort((a, b) => a?.display_order - b?.display_order)
            ?.map(r => r?.reference_text)
            ?.filter(Boolean) || [];

          let dateLabel;
          if (idx === 0) dateLabel = 'Today';
          else if (idx === 1) dateLabel = 'Tomorrow';
          else {
            const d = new Date();
            d?.setDate(d?.getDate() + idx);
            dateLabel = d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }

          return {
            date: dateLabel,
            dayNumber: day?.day_number,
            title: day?.title,
            passages: refs
          };
        });
        setUpcomingReadings(readings);
      }
    } catch (error) {
      console.error('Error fetching upcoming readings:', error);
    }
  };

  const fetchReadingStats = async (enrollment) => {
    try {
      const { data: progressData } = await supabase
        ?.from('user_reading_progress')
        ?.select('id, is_completed, reading_plan_references(book_name, chapter, verse_start, verse_end)')
        ?.eq('user_reading_plan_id', enrollment?.id)
        ?.eq('is_completed', true);

      if (!progressData) return;

      let versesRead = 0;
      let chaptersCompleted = 0;
      const booksStarted = new Set();

      progressData?.forEach(p => {
        const ref = p?.reading_plan_references;
        if (!ref) return;
        if (ref?.book_name) booksStarted?.add(ref?.book_name);
        if (ref?.verse_start && ref?.verse_end) {
          versesRead += (ref?.verse_end - ref?.verse_start + 1);
          chaptersCompleted++;
        } else if (ref?.chapter) {
          versesRead += 26;
          chaptersCompleted++;
        }
      });

      const completedDays = enrollment?.completed_days || 0;
      const totalMinutes = completedDays * 15;

      setReadingStats({
        versesRead: versesRead > 0 ? versesRead : progressData?.length * 26,
        chaptersCompleted: chaptersCompleted > 0 ? chaptersCompleted : progressData?.length,
        booksStarted: booksStarted?.size,
        totalTimeSpent: totalMinutes > 0 ? `${(totalMinutes / 60)?.toFixed(1)}h` : '—'
      });
    } catch (error) {
      console.error('Error fetching reading stats:', error);
    }
  };

  const handleViewPlans = () => navigate('/bible-reading-plans');

  const weeklyPercentage = Math.min((weeklyProgress?.completed / weeklyProgress?.total) * 100, 100);

  // Pace calculation
  const startDate = activePlan?.start_date ? new Date(activePlan.start_date) : new Date();
  const today = new Date();
  const totalDays = activePlan?.reading_plans?.duration_days || 365;
  const daysElapsed = Math.max(1, Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1);
  const expectedProgress = Math.min(100, (daysElapsed / totalDays) * 100);
  const actualProgress = Math.min(activePlan?.progress || 0, 100);
  const isAhead = actualProgress >= expectedProgress;

  if (loading) {
    return (
      <div className={`bg-card rounded-lg shadow-warm ${className}`}>
        <div className="border-b border-border p-4">
          <div className="flex items-center space-x-2">
            <Icon name="Target" size={18} color="var(--color-primary)" />
            <h3 className="font-heading font-semibold text-foreground">Reading Progress</h3>
          </div>
        </div>
        <div className="p-4 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!activePlan) {
    return (
      <div className={`bg-card rounded-lg shadow-warm ${className}`}>
        <div className="border-b border-border p-4">
          <div className="flex items-center space-x-2">
            <Icon name="Target" size={18} color="var(--color-primary)" />
            <h3 className="font-heading font-semibold text-foreground">Reading Progress</h3>
          </div>
        </div>
        <div className="p-6 text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
              <Icon name="BookOpen" size={32} color="var(--color-muted-foreground)" />
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-heading font-semibold text-foreground">No Active Plans</h4>
            <p className="text-sm text-muted-foreground">
              Start a Bible reading plan to track your progress and build consistent reading habits.
            </p>
          </div>
          <Button variant="primary" size="md" onClick={handleViewPlans} iconName="Plus" iconPosition="left" className="w-full">
            Browse Reading Plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg shadow-warm ${className}`}>
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Target" size={18} color="var(--color-primary)" />
            <h3 className="font-heading font-semibold text-foreground">Reading Progress</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={handleViewPlans} iconName="Settings" iconPosition="right">
            Plans
          </Button>
        </div>
      </div>
      <div className="p-4 space-y-5">
        {/* Current Plan */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Current Plan</p>
            <span className="text-xs px-2 py-0.5 bg-success/10 text-success rounded-md">Active</span>
          </div>
          <p className="text-base font-heading text-primary leading-tight">
            {activePlan?.reading_plans?.name || 'Reading Plan'}
          </p>
        </div>

        {/* Overall Progress with Pace */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Overall Progress</p>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${isAhead ? 'text-success' : 'text-warning'}`}>
                {isAhead ? `+${(actualProgress - expectedProgress)?.toFixed(1)}%` : `-${(expectedProgress - actualProgress)?.toFixed(1)}%`}
              </span>
              <p className="text-sm font-mono text-muted-foreground">{Math.round(actualProgress)}%</p>
            </div>
          </div>

          {/* Pace bar */}
          <div className="relative w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <div
              className="absolute top-0 left-0 h-2.5 rounded-full bg-muted-foreground/25 transition-all duration-300"
              style={{ width: `${expectedProgress}%` }}
            />
            <div
              className={`absolute top-0 left-0 h-2.5 rounded-full transition-all duration-300 ${isAhead ? 'bg-success' : 'bg-primary'}`}
              style={{ width: `${actualProgress}%` }}
            />
            <div
              className="absolute top-0 h-2.5 w-0.5 bg-foreground/40"
              style={{ left: `${expectedProgress}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>{activePlan?.completedReferences || 0} of {activePlan?.totalReferences || 0} readings</span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-1.5 rounded-sm bg-muted-foreground/30"></span>
              Pace {expectedProgress?.toFixed(0)}%
            </span>
          </div>
        </div>

        {/* Reading Streak */}
        <div className="flex items-center justify-between p-3 bg-success/10 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Flame" size={16} color="var(--color-success)" />
            <div>
              <p className="text-sm font-medium text-success">Reading Streak</p>
              <p className="text-xs text-success/80">Keep it going!</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-mono font-semibold text-success">{activePlan?.streakDays || 0}</p>
            <p className="text-xs text-success/80">days</p>
          </div>
        </div>

        {/* Reading Statistics */}
        {readingStats && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Reading Statistics</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-muted/30 rounded-lg p-2.5 text-center">
                <p className="text-base font-bold text-foreground">{readingStats?.versesRead?.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">Verses Read</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5 text-center">
                <p className="text-base font-bold text-foreground">{readingStats?.chaptersCompleted}</p>
                <p className="text-[10px] text-muted-foreground">Chapters Done</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5 text-center">
                <p className="text-base font-bold text-foreground">{readingStats?.booksStarted}</p>
                <p className="text-[10px] text-muted-foreground">Books Started</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5 text-center">
                <p className="text-base font-bold text-foreground">{readingStats?.totalTimeSpent}</p>
                <p className="text-[10px] text-muted-foreground">Time Spent</p>
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Readings */}
        {upcomingReadings?.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Upcoming Readings</p>
            <div className="space-y-1.5">
              {upcomingReadings?.slice(0, 3)?.map((reading, idx) => (
                <div
                  key={idx}
                  className={`p-2.5 rounded-lg border text-xs ${idx === 0 ? 'bg-primary/5 border-primary/20' : 'bg-muted/20 border-border'}`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`font-semibold ${idx === 0 ? 'text-primary' : 'text-foreground'}`}>
                      {reading?.date}
                    </span>
                    <span className="text-muted-foreground">Day {reading?.dayNumber}</span>
                  </div>
                  <p className="text-muted-foreground truncate">
                    {reading?.passages?.slice(0, 2)?.join(', ')}
                    {reading?.passages?.length > 2 && ` +${reading?.passages?.length - 2}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Quick Actions</p>
          <div className="grid grid-cols-1 gap-2">
            <Button variant="outline" size="sm" iconName="Calendar" iconPosition="left" className="justify-start rounded-full" onClick={handleViewPlans}>
              View Reading Schedule
            </Button>
            <Button variant="outline" size="sm" iconName="BarChart3" iconPosition="left" className="justify-start rounded-full" onClick={() => navigate('/study-analytics-dashboard')}>
              Progress Statistics
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingProgressTracker;